import { Button, FileInput, rem, Stack, Text, Tooltip } from "@mantine/core";
import { IconPhoto, IconUpload } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { SafeImage } from "./SafeImage ";

interface ImageUploaderProps {
  multiple?: boolean;
  comments?: string;
  acceptedTypes?: string[]; // MIME types: ['image/jpeg', 'image/png']
  overrideFilename?: (file: File) => string;
  overridePath?: (file: File) => string;
  onUpload?: (files: File[], paths?: string[]) => void;
  showUploadButton?: boolean;
  uploadTrigger?: Date | null;
}

export const ImageUploader = ({
  multiple = false,
  comments,
  acceptedTypes = ["image/jpeg", "image/png"],
  overrideFilename,
  overridePath,
  onUpload,
  uploadTrigger,
  showUploadButton,
}: ImageUploaderProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const validateType = (file: File) =>
    acceptedTypes.length === 0 || acceptedTypes.includes(file.type);

  const handleChange = (fileList: File | File[] | null) => {
    const selected = Array.isArray(fileList)
      ? fileList
      : fileList
      ? [fileList]
      : [];

    const filtered = selected.filter(validateType);

    setFiles(filtered);
    if (filtered.length !== selected.length) {
      alert("Some files were skipped due to invalid type.");
    }
  };
  useEffect(() => {
    if (uploadTrigger !== null) {
      handleUpload();
    }
  }, [uploadTrigger]);

  const handleUpload = () => {
    const paths = files.map((file) => {
      const filename = overrideFilename ? overrideFilename(file) : file.name;
      const path = overridePath ? overridePath(file) : `/images/${filename}`;

      return path; // This now includes overridden filename in the default path
    });

    onUpload?.(files, paths);
  };

  return (
    <Stack>
      {comments && (
        <Text size="sm" color="dimmed">
          {comments}
        </Text>
      )}
      <FileInput
        accept={acceptedTypes.join(",")}
        placeholder="Choose image"
        leftSection={<IconPhoto style={{ width: rem(16), height: rem(16) }} />}
        value={multiple ? files : files[0] ?? null}
        multiple={multiple}
        onChange={handleChange}
        clearable
      />
      {files.map((file, idx) => {
        const filename = overrideFilename ? overrideFilename(file) : file.name;
        const path = overridePath ? overridePath(file) : `/images`;
        const label = `${path}/${filename}`;

        return (
          <>
            <Tooltip key={idx} label={label} withArrow>
              <SafeImage
                src={URL.createObjectURL(file)}
                width={120}
                height={120}
                radius="md"
                alt={`preview-${idx}`}
              />
            </Tooltip>
          </>
        );
      })}
      {(showUploadButton ?? true) && (
        <Button
          leftSection={<IconUpload size={16} />}
          onClick={handleUpload}
          disabled={files.length === 0}
        >
          Upload
        </Button>
      )}
    </Stack>
  );
};
